import { useState } from 'react'
import EditableValue from './EditableValue.jsx'
import {
  useSyncEoBuyouts,
  useSubgoalManualClose,
} from '../mutations/useEoBuyoutsSync.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'
import { timeAgo } from '../data/metaKeys.js'

/**
 * SyncedSubGoalTable — sub-goal table for goals where goal.syncSource is
 * set. Today the only source is 'metabase:eo-buyouts' but the structure
 * accommodates future ones.
 *
 * Layout:
 *   1. Sync indicator + "Sync now" button
 *   2. Amber banner explaining v1 limitations (date proxy + Teamwork
 *      completion lag) — gated by canEdit so archive view doesn't show
 *      the action affordances
 *   3. Quarter filter line (informational; the server filters by current
 *      quarter)
 *   4. Header row + sub-goal rows: deal | owner | end_date | status |
 *      update | mark-closed-action
 *
 * Each sub-goal row's status is derived from:
 *   - sub.completedAt   (Teamwork completion — wins)
 *   - sub.manualClosedAt (dashboard override — wins if completedAt null)
 *   - sub.endDate < today (auto-closed: planned transaction date passed,
 *                          assume the deal closed since CT projects use
 *                          end_date as the expected transaction date)
 *   - else: Open
 *
 * The Mark closed button toggles manualClosedAt via /goals/subgoal-override
 * — useful for early closures (deal closed before its planned end_date).
 */
export default function SyncedSubGoalTable({ pillarName, goalIndex, goal, subgoals }) {
  const readOnly = useIsReadOnly()
  const canEdit = !readOnly
  const syncOp = useSyncEoBuyouts()

  const lastSyncedAt = goal?.lastSyncedAt
  const subgoalArr = Array.isArray(subgoals) ? subgoals : []

  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Top bar — sync indicator + Sync now */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          background: '#fff',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 9px',
            borderRadius: 99,
            background: '#ECFEFF',
            color: '#155E75',
            border: '1px solid #A5F3FC',
          }}
        >
          <SyncIcon />
          Synced from Metabase
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {lastSyncedAt
            ? `last synced ${timeAgoSafe(lastSyncedAt)}`
            : 'not synced yet'}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={() => syncOp.mutate()}
            disabled={syncOp.isPending}
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 6,
              background: syncOp.isPending ? '#f3f4f6' : '#fff',
              color: 'var(--muted)',
              border: '1px solid var(--border)',
              cursor: syncOp.isPending ? 'wait' : 'pointer',
            }}
          >
            {syncOp.isPending ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>

      {/* v1 limitations banner — shown until the data team closes both
          gaps. Gated by canEdit because archive view shouldn't render
          action language. */}
      {canEdit && goal.syncSource === 'metabase:eo-buyouts' && (
        <V1LimitationsBanner />
      )}

      {/* Header row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 80px 110px 90px 1.6fr 90px',
          gap: 10,
          padding: '8px 12px',
          background: '#f4f4f5',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        <span>Deal</span>
        <span>Owner</span>
        <span>
          End date{' '}
          <span
            style={{
              fontWeight: 400,
              textTransform: 'none',
              color: 'var(--muted-light)',
              letterSpacing: 0,
            }}
          >
            (proxy)
          </span>
        </span>
        <span>Status</span>
        <span style={{ paddingLeft: 9 }}>Update</span>
        <span></span>
      </div>

      {/* Sub-goal rows */}
      {subgoalArr.length === 0 ? (
        <div
          style={{
            padding: 18,
            fontSize: 12,
            color: 'var(--muted)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          {lastSyncedAt
            ? 'No matching projects found in Teamwork this quarter.'
            : 'Waiting for first sync…'}
        </div>
      ) : (
        subgoalArr.map((sub, i) => (
          <SyncedSubGoalRow
            key={sub.external_id || i}
            sub={sub}
            pillarName={pillarName}
            goalIndex={goalIndex}
            subgoalIndex={i}
            canEdit={canEdit}
          />
        ))
      )}

      {/* Footer hint */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px dashed var(--border)',
          fontSize: 10,
          color: 'var(--muted)',
          fontStyle: 'italic',
        }}
      >
        Sub-goals are managed in Teamwork. Add or remove deals there; they sync
        every 10 minutes. Update column and Mark closed override stay editable here.
      </div>
    </div>
  )
}

/* ─── Banner ─────────────────────────────────────────────────────────── */

function V1LimitationsBanner() {
  return (
    <div
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 6,
        padding: '8px 12px',
        margin: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2">
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      </span>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#78350f' }}>
          How status is calculated.
        </p>
        <ul style={{ margin: '3px 0 0', paddingLeft: 18, fontSize: 10, color: '#92400e', lineHeight: 1.45 }}>
          <li>
            Dates come from each project's <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>end_date</code> in Teamwork — used
            as the planned transaction date until the "Expected Transaction Date" custom field gets
            synced into Metabase.
          </li>
          <li>
            A deal flips to <strong>Closed</strong> automatically once its end_date has passed. For early
            closures (deal closed <em>before</em> its planned end_date), click <strong>Mark closed</strong> on the row.
            The override clears automatically once Teamwork catches up.
          </li>
        </ul>
        <p style={{ margin: '4px 0 0', fontSize: 10, color: '#92400e' }}>
          Working with the data team to close both gaps.
        </p>
      </div>
    </div>
  )
}

/* ─── Row ────────────────────────────────────────────────────────────── */

function SyncedSubGoalRow({ sub, pillarName, goalIndex, subgoalIndex, canEdit }) {
  const [hover, setHover] = useState(false)
  const closeOp = useSubgoalManualClose()

  const teamworkClosed = !!sub.completedAt
  const manuallyClosed = !!sub.manualClosedAt
  const endDateMs = sub.endDate ? new Date(sub.endDate).getTime() : null
  // Auto-closed: end_date has passed. CT projects' end_date is the
  // expected transaction date, so once it's gone we treat the deal
  // as transacted unless explicitly told otherwise.
  const endDatePast = !!endDateMs && endDateMs < Date.now()
  const isClosed = teamworkClosed || manuallyClosed || endDatePast
  const isManualOnly = !teamworkClosed && manuallyClosed && !endDatePast

  let status, statusColor, statusBg
  if (isClosed) {
    status = 'Closed'
    statusColor = '#166534'
    statusBg = '#16A34A'
  } else {
    status = 'Open'
    statusColor = 'var(--muted)'
    statusBg = '#94A3B8'
  }

  const handleMarkClosed = () => {
    closeOp.mutate({
      pillarName,
      goalIndex,
      subgoalIndex,
      manualClosed: !manuallyClosed,
    })
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 80px 110px 90px 1.6fr 90px',
        gap: 10,
        padding: '10px 12px',
        alignItems: 'center',
        borderTop: '1px solid var(--border)',
        background: isClosed ? '#f0fdf4' : '#fff',
        fontSize: 12,
      }}
    >
      <span title={sub.rawName || sub.subgoal} style={{ fontWeight: 600, color: 'var(--text)' }}>
        {sub.subgoal || '(unnamed)'}
      </span>
      <span style={{ color: 'var(--muted)', fontSize: 11 }}>{sub.owner || '—'}</span>
      <span
        style={{
          color: 'var(--muted)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 11,
        }}
      >
        {fmtEndDate(sub.endDate)}
      </span>
      <span>
        <span
          title={
            isManualOnly
              ? 'Marked closed in the dashboard. Will sync directly from Teamwork once the project is marked complete there.'
              : ''
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontWeight: 600,
            color: statusColor,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusBg }} />
          {status}
          {isManualOnly && <span style={{ marginLeft: 1, color: '#15803D', fontWeight: 400 }}>*</span>}
        </span>
      </span>
      <span>
        <EditableValue
          pillarName={pillarName}
          goalIndex={goalIndex}
          subgoalIndex={subgoalIndex}
          field="update"
          value={sub.update || ''}
          inputType="text"
          placeholder="Add an update…"
          multiline
          readonly={!canEdit}
          style={{ fontSize: 11 }}
        />
      </span>
      <span style={{ textAlign: 'right' }}>
        {canEdit && (
          <button
            type="button"
            onClick={handleMarkClosed}
            disabled={closeOp.isPending}
            title={
              isManualOnly
                ? 'Clear the manual close override'
                : isClosed
                  ? 'Marked complete in Teamwork — no override needed'
                  : 'Mark this deal closed in the dashboard until Teamwork catches up'
            }
            style={{
              fontSize: 10,
              padding: '3px 8px',
              borderRadius: 99,
              background: isManualOnly ? '#fef3c7' : '#fff',
              color: isManualOnly ? '#92400e' : 'var(--muted)',
              border: `1px solid ${isManualOnly ? '#fde68a' : 'var(--border)'}`,
              cursor: closeOp.isPending ? 'wait' : (teamworkClosed ? 'not-allowed' : 'pointer'),
              opacity: teamworkClosed ? 0.45 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {isManualOnly ? 'Undo manual' : isClosed ? 'Closed' : 'Mark closed'}
          </button>
        )}
      </span>
    </div>
  )
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

function SyncIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M21 12a9 9 0 11-3-6.7" />
      <polyline points="21 4 21 12 13 12" />
    </svg>
  )
}

function fmtEndDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return '—'
  }
}

function timeAgoSafe(iso) {
  try {
    return timeAgo(new Date(iso).getTime())
  } catch {
    return ''
  }
}
